import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, Search, XCircle, Monitor, Building2,
  ClipboardList, Users, RefreshCw,
} from "lucide-react";
import { apiGet, apiPost } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

function formatDate(value) {
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ts));
}

function normalizeText(value) {
  return String(value || "").trim();
}

const TABS = [
  { id: "overview",       label: "Overview"       },
  { id: "requests",       label: "Registrations"  },
  { id: "tenants",        label: "Tenants"         },
];

export default function AdminDashboard() {
  const [requests, setRequests] = useState([]);
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  const [tab,              setTab]              = useState("overview");
  const [requestSearch,    setRequestSearch]    = useState("");
  const [tenantSearch,     setTenantSearch]     = useState("");
  const [tenantStatusFilter, setTenantStatusFilter] = useState("all");
  const [selectedRequestId,  setSelectedRequestId]  = useState("");
  const [selectedTenantId,   setSelectedTenantId]   = useState("");

  useEffect(() => { void fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, tenRes] = await Promise.all([
        apiGet("/api/admin/requests"),
        apiGet("/api/admin/tenants"),
      ]);
      if (reqRes.ok) setRequests(await reqRes.json());
      if (tenRes.ok) setTenants(await tenRes.json());
    } catch {
      toast.error("Failed to load admin control center data.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this registration and create a tenant workspace?")) return;
    try {
      const res  = await apiPost(`/api/admin/approve/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.message || "Approval failed"); return; }
      toast.success("Registration approved and tenant created.");

      // Surface the seeded "download@<domain>" login (or why one wasn't issued) —
      // this is otherwise invisible since the credentials are only ever generated
      // here and never shown again.
      if (data.seededDownloadAccount?.email) {
        toast.info(`Download login created: ${data.seededDownloadAccount.email} (password: ${data.seededDownloadAccount.password})`);
      } else if (data.downloadAccountStatus === "already_exists") {
        toast.info("Download login not created: that domain's download@ address already belongs to another tenant.");
      } else if (data.downloadAccountStatus === "no_domain") {
        toast.info("Download login not created: the registration email has no usable domain.");
      }

      void fetchData();
    } catch {
      toast.error("Critical error while approving registration.");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Reject this registration request? This cannot be undone.")) return;
    try {
      const res  = await apiPost(`/api/admin/reject/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.message || "Rejection failed"); return; }
      toast.success(data.message || "Registration request rejected.");
      void fetchData();
    } catch {
      toast.error("Critical error while rejecting registration.");
    }
  };

  /* ── filtered lists ── */
  const filteredRequests = useMemo(() => {
    const q = normalizeText(requestSearch).toLowerCase();
    return [...requests]
      .filter(r => !q || [r?.name, r?.email, r?.companyName].some(v => normalizeText(v).toLowerCase().includes(q)))
      .sort((a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime());
  }, [requestSearch, requests]);

  const filteredTenants = useMemo(() => {
    const q = normalizeText(tenantSearch).toLowerCase();
    return [...tenants]
      .filter(t => {
        const matchSearch = !q || [t?.name, t?.ownerEmail, t?.enrollmentKey].some(v => normalizeText(v).toLowerCase().includes(q));
        const matchStatus =
          tenantStatusFilter === "all"
          || (tenantStatusFilter === "active"   &&  t?.isActive)
          || (tenantStatusFilter === "disabled" && !t?.isActive);
        return matchSearch && matchStatus;
      })
      .sort((a, b) => normalizeText(a?.name).localeCompare(normalizeText(b?.name)));
  }, [tenantSearch, tenantStatusFilter, tenants]);

  useEffect(() => {
    if (selectedRequestId && !filteredRequests.some(r => r._id === selectedRequestId))
      setSelectedRequestId("");
  }, [filteredRequests]);

  useEffect(() => {
    if (selectedTenantId && !filteredTenants.some(t => t.id === selectedTenantId))
      setSelectedTenantId("");
  }, [filteredTenants]);

  const selectedRequest = filteredRequests.find(r => r._id === selectedRequestId) || null;
  const selectedTenant  = filteredTenants.find(t => t.id === selectedTenantId)    || null;

  const summary = useMemo(() => {
    const active   = tenants.filter(t => t?.isActive).length;
    const disabled = Math.max(0, tenants.length - active);
    const devices  = tenants.reduce((s, t) => s + Number(t?.deviceCount || 0), 0);
    return { requests: requests.length, active, disabled, devices };
  }, [requests, tenants]);

  /* ── render ── */
  if (loading) {
    return (
      <div className="admin-workspace">
        <div className="admin-empty-state">Loading admin control center…</div>
      </div>
    );
  }

  return (
    <div className="admin-workspace">

      {/* ── Page header ── */}
      <div className="admin-shell-header">
        <div className="admin-shell-copy">
          <span className="admin-shell-eyebrow">Admin Center</span>
          <h1 className="admin-shell-title">Overview</h1>
          <p className="admin-shell-subtitle">
            Registration pipeline, tenant portfolio, and platform health at a glance.
          </p>
        </div>
        <div className="admin-shell-actions">
          <button className="gc-icon-btn" onClick={fetchData} data-tip="Refresh" title="Refresh data">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* ── Pivot tabs ── */}
      <div className="admin-tabbar">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            className={`admin-tab${tab === t.id ? " is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === "requests" && requests.length > 0 && (
              <span className="admin-tab-badge">{requests.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════════════
          Tab: Overview
          ════════════════════════════ */}
      {tab === "overview" && (
        <div className="admin-stack-md">
          <div className="admin-summary-grid">
            <div className="admin-summary-card">
              <div className="admin-summary-icon"><ClipboardList size={20} /></div>
              <div className="admin-summary-copy">
                <span className="admin-summary-label">Pending Requests</span>
                <span className="admin-summary-value">{summary.requests}</span>
                <span className="admin-summary-meta">Awaiting review</span>
              </div>
            </div>
            <div className="admin-summary-card">
              <div className="admin-summary-icon"><Building2 size={20} /></div>
              <div className="admin-summary-copy">
                <span className="admin-summary-label">Active Tenants</span>
                <span className="admin-summary-value">{summary.active}</span>
                <span className="admin-summary-meta">Live workspaces</span>
              </div>
            </div>
            <div className="admin-summary-card">
              <div className="admin-summary-icon" style={{ background: "var(--as-danger-bg)", color: "var(--as-danger)" }}>
                <Users size={20} />
              </div>
              <div className="admin-summary-copy">
                <span className="admin-summary-label">Disabled Tenants</span>
                <span className="admin-summary-value">{summary.disabled}</span>
                <span className="admin-summary-meta">Access suspended</span>
              </div>
            </div>
            <div className="admin-summary-card">
              <div className="admin-summary-icon" style={{ background: "var(--as-success-bg)", color: "var(--as-success)" }}>
                <Monitor size={20} />
              </div>
              <div className="admin-summary-copy">
                <span className="admin-summary-label">Tracked Devices</span>
                <span className="admin-summary-value">{summary.devices}</span>
                <span className="admin-summary-meta">Across all tenants</span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ════════════════════════════
          Tab: Registration Requests
          ════════════════════════════ */}
      {tab === "requests" && (
        <div className="admin-panel">
          <div className="admin-panel-head">
            <div>
              <h2 className="admin-panel-title">Registration approvals</h2>
              <p className="admin-panel-note">Review and action incoming registration requests. Select a row to see full details.</p>
            </div>
            <span className="admin-panel-kicker">{filteredRequests.length} visible</span>
          </div>

          <div className="admin-toolbar">
            <label className="admin-search" aria-label="Search registration requests">
              <Search size={15} />
              <input
                type="search"
                placeholder="Search name, email, or company…"
                value={requestSearch}
                onChange={e => setRequestSearch(e.target.value)}
              />
            </label>
          </div>

          {filteredRequests.length === 0 ? (
            <div className="admin-empty-state">No registration requests match the current search.</div>
          ) : (
            <div className="admin-split-layout">
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Registrant</th>
                      <th>Company</th>
                      <th>Submitted</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map(r => (
                      <tr
                        key={r._id}
                        className={selectedRequestId === r._id ? "is-selected" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedRequestId(r._id)}
                      >
                        <td>
                          <div className="admin-cell-title">{r.name || "Unnamed"}</div>
                          <div className="admin-cell-subtitle">{r.email || "-"}</div>
                        </td>
                        <td><div className="admin-cell-title">{r.companyName || "-"}</div></td>
                        <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{formatDate(r.createdAt)}</div></td>
                        <td>
                          <span className="admin-status is-pending">Pending</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <aside className="admin-detail-pane">
                {selectedRequest ? (
                  <>
                    <h3>{selectedRequest.name || "Unnamed requester"}</h3>
                    <p className="admin-detail-note">Approve to create a tenant workspace, or reject to dismiss this request.</p>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-item">
                        <span>Email</span>
                        <strong>{selectedRequest.email || "-"}</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Company</span>
                        <strong>{selectedRequest.companyName || "-"}</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Submitted</span>
                        <strong>{formatDate(selectedRequest.createdAt)}</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Status</span>
                        <strong>Pending review</strong>
                      </div>
                    </div>
                    <div className="admin-detail-actions">
                      <button type="button" className="admin-btn" onClick={() => handleApprove(selectedRequest._id)}>
                        <CheckCircle2 size={14} />
                        Approve
                      </button>
                      <button type="button" className="admin-btn-danger" onClick={() => handleReject(selectedRequest._id)}>
                        <XCircle size={14} />
                        Reject
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="admin-empty-state">Select a request to view details.</div>
                )}
              </aside>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════
          Tab: Tenants
          ════════════════════════════ */}
      {tab === "tenants" && (
        <div className="admin-panel">
          <div className="admin-panel-head">
            <div>
              <h2 className="admin-panel-title">Tenant portfolio</h2>
              <p className="admin-panel-note">Browse all tenant workspaces. Select a row to see ownership and license details.</p>
            </div>
            <div className="admin-inline-metrics">
              <div className="admin-metric-card">
                <span className="admin-metric-label">Active</span>
                <strong className="admin-metric-value">{summary.active}</strong>
              </div>
              <div className="admin-metric-card">
                <span className="admin-metric-label">Disabled</span>
                <strong className="admin-metric-value">{summary.disabled}</strong>
              </div>
            </div>
          </div>

          <div className="admin-toolbar">
            <label className="admin-search" aria-label="Search tenants">
              <Search size={15} />
              <input
                type="search"
                placeholder="Search company, owner, or enrollment key…"
                value={tenantSearch}
                onChange={e => setTenantSearch(e.target.value)}
              />
            </label>
            <select
              className="admin-filter"
              value={tenantStatusFilter}
              onChange={e => setTenantStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="disabled">Disabled only</option>
            </select>
          </div>

          {filteredTenants.length === 0 ? (
            <div className="admin-empty-state">No tenant records match the current filters.</div>
          ) : (
            <div className="admin-split-layout">
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Status</th>
                      <th>Devices</th>
                      <th>Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map(t => (
                      <tr
                        key={t.id}
                        className={selectedTenantId === t.id ? "is-selected" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedTenantId(t.id)}
                      >
                        <td>
                          <div className="admin-cell-title">{t.name || "Unnamed tenant"}</div>
                          <div className="admin-cell-subtitle">{t.ownerEmail || "-"}</div>
                        </td>
                        <td>
                          <span className={`admin-status ${t.isActive ? "is-active" : "is-disabled"}`}>
                            {t.isActive ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td>
                          <div className="admin-cell-title">{Number(t.deviceCount || 0)}</div>
                        </td>
                        <td>
                          {t.activePlanName
                            ? <span className="admin-chip">{t.activePlanName}</span>
                            : <span className="admin-chip">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <aside className="admin-detail-pane">
                {selectedTenant ? (
                  <>
                    <h3>{selectedTenant.name || "Unnamed tenant"}</h3>
                    <p className="admin-detail-note">Ownership, enrollment, and license entry points.</p>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-item">
                        <span>Owner</span>
                        <strong>{selectedTenant.ownerEmail || "-"}</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Enrollment Key</span>
                        <strong className="admin-monospace">{selectedTenant.enrollmentKey || "-"}</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Device Count</span>
                        <strong>{Number(selectedTenant.deviceCount || 0)} devices</strong>
                      </div>
                      <div className="admin-detail-item">
                        <span>Seat Cap</span>
                        <strong>{selectedTenant.maxSeats === null ? "Unlimited" : Number(selectedTenant.maxSeats || 5)}</strong>
                      </div>
                      {selectedTenant.activePlanName && (
                        <div className="admin-detail-item">
                          <span>Active Plan</span>
                          <strong>{selectedTenant.activePlanName}</strong>
                        </div>
                      )}
                    </div>
                    <div className="admin-detail-actions">
                      <button
                        type="button"
                        className="admin-btn"
                        onClick={() => { window.location.href = `/admin/tenants/${selectedTenant.id}`; }}
                      >
                        Manage Workspace
                      </button>
                      <button
                        type="button"
                        className="admin-btn-secondary"
                        onClick={() => { window.location.href = "/admin/licenses"; }}
                      >
                        Manage Licenses
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="admin-empty-state">Select a tenant to view details.</div>
                )}
              </aside>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
